import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import JdUpload from "../components/JdUpload";
import { toast } from "sonner";

export default function RequisitionNew() {
  const nav = useNavigate();
  const [form, setForm] = useState({
    title: "",
    department: "",
    location: "",
    employment_type: "full_time",
    headcount: 1,
    experience_min: 0,
    experience_max: 0,
    ctc_min: "",
    ctc_max: "",
    skills: "",
    description: "",
    urgency: "medium",
    jd_file_b64: "",
    jd_filename: "",
    jd_mime: "",
  });
  const [busy, setBusy] = useState(false);
  const change = (k) => (e) => {
    const v = e.target.type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value;
    setForm({ ...form, [k]: v });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (Number(form.experience_max) > 0 && Number(form.experience_min) > Number(form.experience_max)) {
      return toast.error("Minimum experience cannot exceed maximum experience");
    }
    if (form.ctc_min !== "" && form.ctc_max !== "" && Number(form.ctc_min) > Number(form.ctc_max)) {
      return toast.error("Minimum CTC cannot exceed maximum CTC");
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        ctc_min: form.ctc_min === "" ? null : Number(form.ctc_min),
        ctc_max: form.ctc_max === "" ? null : Number(form.ctc_max),
      };
      const r = await api.post("/requisitions", payload);
      toast.success("Requisition submitted to HR for review");
      nav(`/requisitions/${r.data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-8 max-w-3xl mx-auto" data-testid="requisition-new-page">
      <div className="border-b border-rule pb-6">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-quiet">New Requisition · Step 0</div>
        <h1 className="font-serif text-5xl text-ink mt-2 leading-none">Raise a requisition.</h1>
        <p className="text-quiet mt-3 text-sm max-w-xl">
          HR will review your request within 3 working days. On acceptance, a public application
          link is generated and the role enters the open pipeline.
        </p>
      </div>

      <form onSubmit={submit} className="mt-8 space-y-6 bg-white border border-rule p-6 fade-up">
        <JdUpload form={form} setForm={setForm} />

        <Field label="Role title">
          <input data-testid="req-title" required value={form.title} onChange={change("title")} className={inp} />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Department">
            <input data-testid="req-department" required value={form.department} onChange={change("department")} className={inp} />
          </Field>
          <Field label="Location">
            <input data-testid="req-location" required value={form.location} onChange={change("location")} className={inp} />
          </Field>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <Field label="Headcount">
            <input data-testid="req-headcount" type="number" min={1} value={form.headcount} onChange={change("headcount")} className={inp} />
          </Field>
          <Field label="Employment type">
            <select data-testid="req-employment" value={form.employment_type} onChange={change("employment_type")} className={inp}>
              <option value="full_time">Full time</option>
              <option value="contract">Contract</option>
              <option value="intern">Intern</option>
            </select>
          </Field>
          <Field label="Urgency">
            <select data-testid="req-urgency" value={form.urgency} onChange={change("urgency")} className={inp}>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Min experience (yrs)">
            <input data-testid="req-exp-min" type="number" min={0} value={form.experience_min} onChange={change("experience_min")} className={inp} />
          </Field>
          <Field label="Max experience (yrs)">
            <input data-testid="req-exp-max" type="number" min={form.experience_min || 0} value={form.experience_max} onChange={change("experience_max")} className={inp} />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="CTC min (LPA)">
            <input data-testid="req-ctc-min" type="number" min={0} max={form.ctc_max || undefined} value={form.ctc_min} onChange={change("ctc_min")} className={inp} />
          </Field>
          <Field label="CTC max (LPA)">
            <input data-testid="req-ctc-max" type="number" min={form.ctc_min || 0} value={form.ctc_max} onChange={change("ctc_max")} className={inp} />
          </Field>
        </div>
        <Field label="Skills (comma separated)">
          <input data-testid="req-skills" required value={form.skills} onChange={change("skills")} placeholder="React, TypeScript, GraphQL" className={inp} />
        </Field>
        <Field label="Job description (or rely on uploaded JD above)">
          <textarea data-testid="req-description" rows={8} value={form.description} onChange={change("description")} className={inp} placeholder="Type a short description here, or upload the formal JD file above." />
        </Field>

        <div className="flex justify-end gap-3 pt-4 border-t border-rule">
          <button type="button" onClick={() => nav(-1)} className="border border-ink px-5 py-2.5 text-sm hover:bg-paper">
            Cancel
          </button>
          <button
            data-testid="req-submit"
            type="submit"
            disabled={busy}
            className="bg-klein hover:bg-klein-hover text-white px-5 py-2.5 text-sm font-medium disabled:opacity-50"
          >
            {busy ? "Submitting…" : "Submit to HR →"}
          </button>
        </div>
      </form>
    </div>
  );
}

const inp = "w-full bg-transparent border border-rule px-3 py-2.5 focus:outline-none focus:border-klein focus:ring-1 focus:ring-klein font-sans";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-quiet">{label}</span>
    <div className="mt-1.5">{children}</div>
  </label>
);
